import { useState } from 'react'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import type { ReadinessCheck } from './CfoStatementAiHeadline'

// CfoBooksTrustModal — opens when the user clicks the "Books trust: NN/100" pill. Explains exactly
// which deterministic close checks are lowering the score (e.g. a non-zero Opening Balance Equity),
// and lets the owner immediately tune it: if a flagged check is actually expected / the books really
// are closed, they acknowledge it and the score recomputes on the next fetch (excluding that check).

const CHECK_LABELS: Record<string, string> = {
  opening_balance_equity: 'Opening Balance Equity',
  suspense_ask_my_accountant: 'Suspense / Ask My Accountant',
  undeposited_funds: 'Undeposited Funds',
  bs_ties_out: 'Balance sheet ties out',
}

const humanize = (id: string): string =>
  CHECK_LABELS[id] || id.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())

const fmtAmount = (n: number | null | undefined): string | null => {
  if (n == null || !Number.isFinite(n)) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

const statusTone = (status: string | undefined): { color: string; glyph: string; word: string } => {
  switch (status) {
    case 'fail':
      return { color: 'var(--neg, #b91c1c)', glyph: '✕', word: 'Blocking' }
    case 'warn':
      return { color: '#b7791f', glyph: '▲', word: 'Watch' }
    case 'unavailable':
      return { color: 'var(--muted, #6b7280)', glyph: '?', word: 'Not checked' }
    default:
      return { color: 'var(--pos, #047857)', glyph: '✓', word: 'Clear' }
  }
}

export function CfoBooksTrustModal({
  appApi,
  workbookId,
  period,
  score,
  overall,
  checks,
  onClose,
  onChanged,
}: {
  appApi: ServerApiClient
  workbookId: string
  period: string
  score: number
  overall?: string
  checks: ReadinessCheck[]
  onClose: () => void
  onChanged: () => void
}) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const headline =
    overall === 'clean'
      ? 'Your books look close-ready.'
      : overall === 'not_close_ready'
        ? 'A few things are keeping these books from being close-ready.'
        : overall === 'not_evaluated'
          ? 'The available checks are reflected below; one or more checks could not be run.'
          : 'These books are mostly there, with a couple of things to watch.'

  const setAck = (check: ReadinessCheck, acknowledged: boolean) => {
    setBusyId(check.id)
    setError('')
    appApi
      .acknowledgeQuickBooksReadinessCheck(workbookId, {
        check_id: check.id,
        period,
        acknowledged,
      })
      .then(() => onChanged())
      .catch((e: any) => setError(String(e?.message || e)))
      .finally(() => setBusyId(null))
  }

  // Failing/warning checks first (the actionable ones), then the passing checks for completeness.
  const ranked = [...checks].sort((a, b) => {
    const w = (c: ReadinessCheck) => (c.status === 'fail' ? 0 : c.status === 'warn' ? 1 : 2)
    return w(a) - w(b)
  })
  const actionable = ranked.filter((c) => c.status === 'fail' || c.status === 'warn')

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.32)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rise"
        style={{
          width: 'min(580px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: 'var(--surface-1, #fff)',
          borderRadius: 16,
          padding: '20px 22px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: overall === 'clean' ? 'var(--pos)' : 'var(--neg)',
            }}
          >
            Books trust: {score}/100
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} aria-label="Close" style={{ fontSize: 13 }}>
            ✕
          </button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 14.5, lineHeight: 1.55, color: 'var(--muted, #555)' }}>
          {headline} This score comes from deterministic checks on your books — not the AI's opinion.
          {actionable.length
            ? ' If a flagged check is actually expected, mark it resolved and the score updates right away.'
            : ''}
        </p>

        {error ? <div style={{ fontSize: 13, color: 'var(--neg)', marginBottom: 10 }}>{error}</div> : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ranked.map((c) => {
            const tone = statusTone(c.status)
            const amount = fmtAmount(c.amount)
            const isActionable = c.status === 'fail' || c.status === 'warn'
            const busy = busyId === c.id
            return (
              <div
                key={c.id}
                style={{
                  border: '1px solid var(--border, #eee)',
                  borderRadius: 12,
                  padding: '12px 14px',
                  opacity: c.acknowledged ? 0.72 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span aria-hidden style={{ color: tone.color, fontSize: 13 }}>
                    {tone.glyph}
                  </span>
                  <span style={{ fontSize: 14.5, fontWeight: 600 }}>{humanize(c.id)}</span>
                  {amount ? (
                    <span style={{ fontSize: 13, color: 'var(--muted, #888)' }}>· {amount}</span>
                  ) : null}
                  <span style={{ flex: 1 }} />
                  {c.acknowledged ? (
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--muted, #888)' }}>
                      Acknowledged
                    </span>
                  ) : (
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: tone.color }}>{tone.word}</span>
                  )}
                </div>
                {c.note ? (
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink, #333)' }}>{c.note}</div>
                ) : null}
                {isActionable ? (
                  <div style={{ marginTop: 10 }}>
                    {c.acknowledged ? (
                      <button
                        className="btn ghost"
                        disabled={busy}
                        onClick={() => setAck(c, false)}
                        style={{ fontSize: 12.5 }}
                      >
                        {busy ? 'Updating…' : 'Undo — count this against trust again'}
                      </button>
                    ) : (
                      <button
                        className="btn"
                        disabled={busy}
                        onClick={() => setAck(c, true)}
                        style={{ fontSize: 12.5 }}
                      >
                        {busy ? 'Saving…' : 'This is expected — don’t lower trust for it'}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CfoBooksTrustModal
